import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Icon from '@material-ui/core/Icon';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import webhookService from 'v2/services/webhook';

import type { IWebHookTab } from 'v2/interfaces/webhook';
import type { IWebHookFilterHistoric, IWebHookListHistoric } from 'v2/interfaces/webhook/historic';
import type { IWebHookFilter, IWebHookList } from 'v2/interfaces/webhook/webhook';

import { WebHookContext, WebHookHistoricContext, WebHookScreenContext } from './context';
import FiltersHistoric, { initialValuesWebhookHistoric } from './ContextHistoric/Filters';
import ListHistoric from './ContextHistoric/List';
import FiltersWebhook, { initialValuesWebhook } from './ContextWebhook/Filters';
import ListWebHook from './ContextWebhook/List';
import ModalCreate from './ModalCreate';
import styles from './styles';
import Tabs from './Tabs';

import type IPagination from 'interfaces/IPagination';

const WEBHOOK_DOCUMENTATION_URL = 'https://github.com/eduzz/webhook';

const WebHook = memo(() => {
  const classes = styles();

  const [loading, setLoading] = useState<boolean>(true);
  const [tab, setTab] = useState<IWebHookTab>('webhook');

  const [list, setList] = useState<IWebHookList[]>([]);
  const [listHistoric, setListHistoric] = useState<IWebHookListHistoric[]>([]);
  const [filtersWebhook, setFiltersWebhook] = useState<IWebHookFilter>(initialValuesWebhook);
  const [modalCreate, setModalCreate] = useState<boolean>(false);

  const [filtersHistoric, setFiltersHistoric] = useState<IWebHookFilterHistoric>(initialValuesWebhookHistoric);
  const [pagination, setPagination] = useState<IPagination>(null);
  const [quickView, setQuickView] = useState<boolean>(false);
  const [webhookDetails, setWebhookDetails] = useState<IWebHookListHistoric | null>(null);

  const getList = useCallback(() => {
    setLoading(true);
    if (tab === 'webhook') {
      setFiltersHistoric(initialValuesWebhookHistoric);
      webhookService
        .getList(filtersWebhook)
        .pipe()
        .subscribe((res) => {
          setList(res.data);
          setLoading(false);
        }, toast.error);
      return;
    }
    setFiltersWebhook(initialValuesWebhook);
    webhookService
      .getListHistoric(filtersHistoric, { current_page: 1, per_page: 10 })
      .pipe()
      .subscribe((res) => {
        setPagination(res.pagination);
        setListHistoric(res.data);
        setLoading(false);
      }, toast.error);
  }, [tab, filtersHistoric, filtersWebhook]);

  const updateListHistoric = useCallback(
    (newPagination: IPagination) => {
      setLoading(true);

      webhookService
        .getListHistoric(filtersHistoric, newPagination)
        .pipe()
        .subscribe((res) => {
          setPagination(res.pagination);
          setListHistoric(res.data);
          setLoading(false);
        }, toast.error);
    },
    [filtersHistoric]
  );

  useEffect(() => getList(), [getList]);

  return (
    <>
      <Grid container justify='space-between'>
        <div className='my-title'>
          <h1>Webhooks</h1>
          <p>Receba em sua URL dados sobre mudança no status de suas vendas.</p>
        </div>

        <div className={classes.buttonHeader}>
          <Button href={WEBHOOK_DOCUMENTATION_URL} target='_blank' variant='outlined'>
            Documentação
          </Button>
          <PermissionHide action='myeduzz:webhook:criar'>
            <Button variant='contained' color='secondary' startIcon={<Icon>add</Icon>} onClick={() => setModalCreate(true)}>
              Nova URL
            </Button>
          </PermissionHide>
        </div>
      </Grid>

      <div className={classes.webhook}>
        <WebHookScreenContext.Provider
          value={{
            loading,
            setLoading,
            updateList: getList,
            tab,
            setTab,
          }}
        >
          <Tabs />

          {tab === 'webhook' && (
            <WebHookContext.Provider
              value={{
                list,
                setList,
                updateList: getList,
                filtersWebhook,
                setFiltersWebhook,
              }}
            >
              <FiltersWebhook />
              <ListWebHook />
            </WebHookContext.Provider>
          )}

          {tab === 'historic' && (
            <WebHookHistoricContext.Provider
              value={{
                listHistoric,
                setListHistoric,
                updateListHistoric,

                filtersHistoric,
                setFiltersHistoric,

                pagination,
                setPagination,

                quickView,
                setQuickView,

                webhookDetails,
                setWebhookDetails,
              }}
            >
              <FiltersHistoric />
              <ListHistoric />
            </WebHookHistoricContext.Provider>
          )}

          <ModalCreate open={modalCreate} close={() => setModalCreate(false)} />
        </WebHookScreenContext.Provider>
      </div>
    </>
  );
});

export default WebHook;
